<template>
  <v-card class="vh">
    <div style="display:none">
      <v-dialog v-model="showPeekIntRep">
        <PeekIntRep/>
      </v-dialog>
    </div>
    <div class="vh__head">
      <div class="vh__head-text">
        <div class="vh__title">{{ title }}</div>
        <div class="vh__sub">{{ subtitle }}</div>
      </div>
      <v-btn icon="mdi-close" variant="text" density="comfortable" @click="$emit('close')" :aria-label="$t('close')" />
    </div>

    <v-card-text class="vh__body">
      <div v-if="loading" class="vh__empty">{{ $t('loading') }}</div>
      <div v-else-if="items.length <= 0" class="vh__empty">{{ $t('verif_machines.no_history') }}</div>

      <section v-for="cycle in cycles" :key="cycle.key" class="vh__cycle">
        <div class="vh__cycle-head">
          <v-chip size="small" :color="cycle.closed ? 'default' : 'success'" :variant="cycle.closed ? 'tonal' : 'flat'" label>
            <v-icon start size="small">{{ cycle.closed ? 'mdi-lock-outline' : 'mdi-progress-check' }}</v-icon>
            {{ cycle.closed
                ? $t('verif_machines.history_closed', { date: $filters.formatDate(cycle.report.done_at) })
                : $t('verif_machines.history_current') }}
          </v-chip>
          <v-btn
              v-if="cycle.closed && cycle.report.report"
              size="x-small"
              variant="text"
              prepend-icon="mdi-file-pdf-box"
              :text="$t('verif_machines.actions.closure_report')"
              @click="goToClosureReport(cycle.report.report)"
              />
        </div>

        <ol class="vh__timeline">
          <li v-for="it in cycle.items" :key="it.pk" class="vh__entry" :class="{ 'vh__entry--kept': it.keep_info_after_closure }">
            <div class="vh__dot"></div>
            <div class="vh__entry-main">
              <div class="vh__entry-top">
                <span class="vh__date">{{ $filters.formatDate(it.done_at) }}</span>
                <span class="vh__partners">
                  <v-chip v-for="p in it.partners" :key="p.id" size="x-small" variant="tonal" class="mr-1">{{ p.name }}</v-chip>
                </span>
                <v-tooltip v-if="it.keep_info_after_closure" location="top" :text="$t('verif_machines.keep_info_after_closure')">
                  <template v-slot:activator="{ props }">
                    <v-icon v-bind="props" size="small" color="warning" icon="mdi-pin" />
                  </template>
                </v-tooltip>
                <v-btn
                    v-if="it.intervention_report_id != null"
                    size="x-small"
                    variant="text"
                    color="primary"
                    prepend-icon="mdi-clipboard-file-outline"
                    :text="$t('intervention_reports.label', 1)"
                    @click="goToIntRep(it.intervention_report_id)"
                    />
              </div>
              <div v-if="it.comment" class="vh__comment">{{ it.comment }}</div>
            </div>
          </li>
        </ol>
      </section>
    </v-card-text>
  </v-card>
</template>
<script>
  import PeekIntRep from "@/components/PeekIntRep";

  export default {
    components: {
      PeekIntRep,
    },
    props: {
      verif_id: {},
      producer_id: {},
    },
    emits: ['close'],
    mounted() {
      this.loadHistory();
    },
    methods: {
      loadHistory() {
        this.$store.dispatch('verif_dones/getHistory', {
          producer_id: this.producer_id,
          verif_id: this.verif_id,
        }).then((result)=> this.items = result)
      },
      goToIntRep(pk) {
        this.$store.dispatch('intervention_reports/getItem', pk).then(()=>{
          this.showPeekIntRep = true;
        })
      },
      goToClosureReport(url) {
        window.open(url, "_blank");
      }
    },
    computed: {
      loading() {
        return this.$store.state.verif_dones.isLoading
      },
      first() {
        return this.items[0]
      },
      title() {
        if (!this.first) return this.$t('verif_machines.actions.view_history')
        const v = this.first.verif
        const en = this.$store.getters.language === 'en'
        return (en && v.verification_title_en) || v.verification_title
      },
      subtitle() {
        if (!this.first) return ''
        const v = this.first.verif
        const en = this.$store.getters.language === 'en'
        const cat = (en && v.verification_category.name_en) || v.verification_category.name
        return this.first.producer.display_name + ' · ' + cat
      },
      // one block per cycle: the open one first, then each closure, newest first (items arrive sorted)
      cycles() {
        const byKey = new Map()
        for (const it of this.items) {
          const key = it.closure_report_id ?? 'current'
          if (!byKey.has(key)) byKey.set(key, { key, closed: key !== 'current', report: it.closure_report, items: [] })
          byKey.get(key).items.push(it)
        }
        return [...byKey.values()]
      },
    },
    data() {
      return {
        items: [],
        showPeekIntRep: false,
      }
    }
  }
</script>
<style scoped>
.vh {
  display: flex;
  flex-direction: column;
  height: 85vh;
  max-height: 100%;
}
.vh__head {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 12px 8px 10px 16px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}
.vh__head-text {
  flex: 1 1 auto;
  min-width: 0;
}
.vh__title {
  font-weight: 700;
  font-size: 1.05rem;
  line-height: 1.25;
  overflow-wrap: anywhere;
}
.vh__sub {
  font-size: 0.8rem;
  color: rgba(0, 0, 0, 0.6);
}
.vh__body {
  overflow-y: auto;
  background: #f6f7f9;
  padding: 12px 16px;
}
.vh__empty {
  text-align: center;
  color: rgba(0, 0, 0, 0.55);
  padding: 24px 0;
}
.vh__cycle {
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 10px;
  padding: 10px 12px;
  margin-bottom: 10px;
}
.vh__cycle-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 6px;
}
.vh__timeline {
  list-style: none;
  margin: 0;
  padding: 0 0 0 14px;
  border-left: 2px solid rgba(0, 0, 0, 0.1);
}
.vh__entry {
  position: relative;
  padding: 6px 0 6px 10px;
}
.vh__dot {
  position: absolute;
  left: -20px;
  top: 12px;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #43a047;
  border: 2px solid #fff;
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.15);
}
.vh__entry--kept .vh__dot {
  background: #fb8c00;
}
.vh__entry-top {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
}
.vh__date {
  font-weight: 600;
  font-size: 0.9rem;
}
.vh__comment {
  margin-top: 4px;
  font-size: 0.875rem;
  color: rgba(0, 0, 0, 0.75);
  white-space: pre-wrap;
}
.vh__entry--kept .vh__comment {
  background: #fff8e1;
  border-radius: 6px;
  padding: 6px 8px;
}
</style>
